import { Link, Outlet, useLocation } from 'react-router-dom';
import {
    AppShell,
    Avatar,
    Box,
    Burger,
    Group,
    NavLink,
    ScrollArea,
    Stack,
    Text,
    ThemeIcon,
    Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconBuilding,
    IconChartBar,
    IconClipboardCheck,
    IconClipboardList,
    IconFileReport,
    IconFingerprint,
    IconFlag,
    IconGitMerge,
    IconLayoutDashboard,
    IconLink,
    IconLock,
    IconMapPin,
    IconArrowsSort,
    IconRadar,
    IconRoute,
    IconShield,
    IconUser,
    IconUserCircle,
    IconUsers,
    IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../auth/auth-context';
import { ColorSchemeToggle } from '../components/ColorSchemeToggle';
import { userLabel } from '../lib/labels';

const navGroups: {
    labelKey: string;
    items: { to: string; labelKey: string; icon: typeof IconUser }[];
}[] = [
    {
        labelKey: 'nav.groups.identity',
        items: [
            { to: '/users', labelKey: 'nav.items.users', icon: IconUsers },
            { to: '/organizations', labelKey: 'nav.items.organizations', icon: IconBuilding },
            { to: '/organization-members', labelKey: 'nav.items.orgMembers', icon: IconUsersGroup },
            { to: '/roles', labelKey: 'nav.items.roles', icon: IconShield },
            { to: '/org-roles', labelKey: 'nav.items.orgRoles', icon: IconLock },
            { to: '/user-roles', labelKey: 'nav.items.userRoles', icon: IconFingerprint },
        ],
    },
    {
        labelKey: 'nav.groups.operations',
        items: [
            { to: '/dashboard', labelKey: 'nav.items.dashboard', icon: IconLayoutDashboard },
            { to: '/cases', labelKey: 'nav.items.cases', icon: IconClipboardList },
            { to: '/visits', labelKey: 'nav.items.visits', icon: IconRoute },
            { to: '/qa', labelKey: 'nav.items.qa', icon: IconClipboardCheck },
            { to: '/monitoring', labelKey: 'nav.items.monitoring', icon: IconRadar },
            { to: '/monitoring-links', labelKey: 'nav.items.monitoringLinks', icon: IconLink },
            {
                to: '/assignment-queue',
                labelKey: 'nav.items.assignmentQueue',
                icon: IconArrowsSort,
            },
            { to: '/reports', labelKey: 'nav.items.reports', icon: IconFileReport },
            { to: '/subjects', labelKey: 'nav.items.subjects', icon: IconUser },
            { to: '/pois', labelKey: 'nav.items.pois', icon: IconMapPin },
            { to: '/duplicate-review', labelKey: 'nav.items.duplicateReview', icon: IconGitMerge },
        ],
    },
    {
        labelKey: 'nav.groups.insights',
        items: [
            { to: '/report', labelKey: 'nav.items.report', icon: IconChartBar },
            { to: '/agent-dashboard', labelKey: 'nav.items.agentDashboard', icon: IconUserCircle },
        ],
    },
    {
        labelKey: 'nav.groups.security',
        items: [
            { to: '/audit-logs', labelKey: 'nav.items.auditLogs', icon: IconShield },
            { to: '/feature-flags', labelKey: 'nav.items.featureFlags', icon: IconFlag },
        ],
    },
];

export function AdminLayout() {
    const { t } = useTranslation();
    const [opened, { toggle }] = useDisclosure();
    const location = useLocation();
    const { logout, payload } = useAuth();

    const displayName = userLabel({
        id: payload?.sub ?? payload?.id ?? '',
        email: payload?.email,
        firstName: payload?.firstName ?? t('layout.adminFallbackName'),
        lastName: payload?.lastName ?? '',
    });

    return (
        <AppShell
            header={{ height: 64 }}
            navbar={{ width: 280, breakpoint: 'sm', collapsed: { mobile: !opened } }}
            padding="lg"
        >
            <AppShell.Header>
                <Group h="100%" px="lg" justify="space-between">
                    <Group>
                        <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
                        <Stack gap={0}>
                            <Title order={4} fw={700}>
                                {t('layout.brand')}
                            </Title>
                            <Text size="xs" c="dimmed">
                                {t('layout.subtitle')}
                            </Text>
                        </Stack>
                    </Group>
                    <Group gap="md">
                        <ColorSchemeToggle />
                        <Group gap="sm">
                            <Avatar radius="xl" size="md" color="violet" name={displayName} />
                            <Box visibleFrom="xs">
                                <Text size="sm" fw={500} lineClamp={1}>
                                    {displayName}
                                </Text>
                                <Text
                                    component="button"
                                    size="xs"
                                    c="dimmed"
                                    style={{
                                        background: 'none',
                                        border: 0,
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                    onClick={logout}
                                >
                                    {t('common.actions.signOut')}
                                </Text>
                            </Box>
                        </Group>
                    </Group>
                </Group>
            </AppShell.Header>
            <AppShell.Navbar p="md">
                <ScrollArea>
                    {navGroups.map((group) => (
                        <Box key={group.labelKey} mb="sm">
                            <Text size="xs" tt="uppercase" c="dimmed" fw={700} mb={6} px="sm">
                                {t(group.labelKey)}
                            </Text>
                            {group.items.map((item) => {
                                const Icon = item.icon;
                                const active =
                                    location.pathname === item.to ||
                                    location.pathname.startsWith(`${item.to}/`);
                                return (
                                    <NavLink
                                        key={item.to}
                                        component={Link}
                                        to={item.to}
                                        label={t(item.labelKey)}
                                        active={active}
                                        leftSection={
                                            <ThemeIcon
                                                variant={active ? 'light' : 'subtle'}
                                                size="sm"
                                                radius="md"
                                            >
                                                <Icon size={14} stroke={1.5} />
                                            </ThemeIcon>
                                        }
                                    />
                                );
                            })}
                        </Box>
                    ))}
                </ScrollArea>
            </AppShell.Navbar>
            <AppShell.Main>
                <Outlet />
            </AppShell.Main>
        </AppShell>
    );
}
